import Component from "@glimmer/component";
import { fn } from "@ember/helper";
import { on } from "@ember/modifier";
import { action } from "@ember/object";
import { getOwner } from "@ember/owner";
import { service } from "@ember/service";
import cookie from "discourse/lib/cookie";
import EverythingSectionLink from "discourse/lib/sidebar/common/community-section/everything-section-link";
import { eq } from "discourse/truth-helpers";
import DButton from "discourse/ui-kit/d-button";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import dIcon from "discourse/ui-kit/helpers/d-icon";
import { i18n } from "discourse-i18n";
import CommunitySecondaryNavigationItem from "./community-secondary-navigation-item";

export default class CommunitySecondaryNavigation extends Component {
  @service communityNavigation;
  @service communityNavigationNotices;
  @service topicTrackingState;

  constructor() {
    super(...arguments);
    const owner = getOwner(this);
    this.everythingLink = new EverythingSectionLink(owner, {
      overriddenName: i18n("community_navigation.all_topics"),
    });
    this.topicTrackingCallbackId = this.topicTrackingState?.onStateChange(() =>
      this.everythingLink.onTopicTrackingStateChange()
    );
  }

  willDestroy() {
    if (this.topicTrackingCallbackId) {
      this.topicTrackingState.offStateChange(this.topicTrackingCallbackId);
    }

    this.everythingLink.teardown?.();
    this.communityNavigation.closeMobile({ restoreFocus: false });
    super.willDestroy(...arguments);
  }

  get secondaryTitle() {
    const secondary = this.communityNavigation.secondary;

    if (this.communityNavigation.secondaryKey === "public") {
      return i18n("community_navigation.home_title");
    }

    return this.activeGame?.name || secondary?.title;
  }

  get secondarySubtitle() {
    if (this.communityNavigation.secondaryKey === "public") {
      return i18n("community_navigation.home_subtitle");
    }

    return this.activeGame?.name_en;
  }

  get activeGame() {
    return this.communityNavigation.activeGame;
  }

  @action
  navigate(item, event) {
    this.closeMobile();
    if (item.access_restricted) {
      event?.preventDefault();
      this.communityNavigationNotices.showCreatorAccessRequired();
      return;
    }

    if (!item.requires_login) {
      return;
    }

    event?.preventDefault();
    cookie("destination_url", item.href, { path: "/" });
    getOwner(this).lookup("route:application").send("showLogin");
  }

  @action
  closeMobile() {
    this.communityNavigation.closeMobile();
  }

  @action
  mobileKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault();
      this.closeMobile();
      return;
    }
    if (event.key !== "Tab") {
      return;
    }

    const focusable = [
      ...event.currentTarget.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ),
    ];
    if (!focusable.length) {
      return;
    }

    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  <template>
    {{#if this.communityNavigation.active}}
      {{#if this.activeGame.cover_url}}
        <img
          class="community-game-background"
          src={{this.activeGame.cover_url}}
          alt=""
        />
      {{/if}}
      {{#if this.communityNavigation.mobileOpen}}
        <button
          type="button"
          class="community-navigation-drawer__backdrop"
          aria-label={{i18n "community_navigation.close_navigation"}}
          {{on "click" this.closeMobile}}
        ></button>
        <div
          id="community-navigation-drawer"
          class="community-navigation-drawer"
          role="dialog"
          aria-modal="true"
          aria-label={{i18n "community_navigation.navigation"}}
          tabindex="-1"
          {{on "keydown" this.mobileKeydown}}
        >
          <header class="community-navigation-drawer__header">
            <h2>{{i18n "community_navigation.navigation"}}</h2>
            <DButton
              @action={{this.closeMobile}}
              @icon="xmark"
              @title="community_navigation.close_navigation"
              class="btn-transparent"
            />
          </header>
          <div class="community-navigation-drawer__body">
            <ul class="community-navigation-drawer__primary">
              {{#each this.communityNavigation.tree.primary as |item|}}
                {{#if (eq item.kind "group")}}
                  <li class="community-navigation-drawer__group">
                    <h3>{{item.label}}</h3>
                    <ul>
                      {{#each item.children as |child|}}
                        <li>
                          <a
                            href={{child.href}}
                            class={{dConcatClass
                              "community-navigation-drawer__link"
                              (if
                                (eq
                                  this.communityNavigation.primaryKey child.key
                                )
                                "--active"
                              )
                            }}
                            {{on "click" this.closeMobile}}
                          >
                            {{#if child.icon_url}}
                              <img src={{child.icon_url}} alt="" />
                            {{else}}
                              {{dIcon "gamepad"}}
                            {{/if}}
                            <span>{{child.label}}</span>
                          </a>
                        </li>
                      {{/each}}
                    </ul>
                  </li>
                {{else}}
                  <li>
                    <a
                      href={{item.href}}
                      class={{dConcatClass
                        "community-navigation-drawer__link"
                        (if
                          (eq this.communityNavigation.primaryKey item.key)
                          "--active"
                        )
                      }}
                      {{on "click" (fn this.navigate item)}}
                    >
                      {{dIcon
                        (if
                          (eq item.key "mods")
                          "store"
                          (if
                            (eq item.key "mod")
                            "puzzle-piece"
                            (if
                              (eq item.key "creator")
                              "pen-nib"
                              "layer-group"
                            )
                          )
                        )
                      }}
                      <span>{{item.label}}</span>
                    </a>
                  </li>
                {{/if}}
              {{/each}}
            </ul>

            {{#if this.communityNavigation.secondary}}
              <div class="community-navigation-drawer__secondary">
                <h3>{{this.secondaryTitle}}</h3>
                {{#each
                  this.communityNavigation.secondary.groups
                  as |group|
                }}
                  {{#if group.label}}
                    <h4>{{group.label}}</h4>
                  {{/if}}
                  <ul class="community-secondary-navigation__list">
                    {{#each group.items as |item|}}
                      <CommunitySecondaryNavigationItem
                        @item={{item}}
                        @activeItemKey={{this.communityNavigation.activeItemKey}}
                        @everythingLink={{this.everythingLink}}
                        @onNavigate={{this.navigate}}
                        @onClose={{this.closeMobile}}
                      />
                    {{/each}}
                  </ul>
                {{/each}}
              </div>
            {{/if}}
          </div>
        </div>
      {{/if}}

      {{#if this.communityNavigation.secondary}}
        <nav
          aria-label={{i18n "community_navigation.secondary_navigation"}}
          class="community-secondary-navigation"
        >
          <div class="community-secondary-navigation__desktop">
            <header class="community-secondary-navigation__heading">
              <h2 class="community-secondary-navigation__title">
                {{this.secondaryTitle}}
              </h2>
              {{#if this.secondarySubtitle}}
                <p class="community-secondary-navigation__subtitle">
                  {{this.secondarySubtitle}}
                </p>
              {{/if}}
            </header>
            {{#each this.communityNavigation.secondary.groups as |group|}}
              {{#if group.label}}
                <h3
                  class="community-secondary-navigation__group-title"
                >{{group.label}}</h3>
              {{/if}}
              <ul class="community-secondary-navigation__list">
                {{#each group.items as |item|}}
                  <CommunitySecondaryNavigationItem
                    @item={{item}}
                    @activeItemKey={{this.communityNavigation.activeItemKey}}
                    @everythingLink={{this.everythingLink}}
                    @onNavigate={{this.navigate}}
                    @onClose={{this.closeMobile}}
                  />
                {{/each}}
              </ul>
            {{/each}}
          </div>
        </nav>
      {{/if}}
    {{/if}}
  </template>
}
